import { useGetAssessmentTestSession } from '../../../hooks/use-hydra/admin';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import dayjs from 'dayjs';
import { AssessmentTestSessionDetailResponse } from '../../../api/admin';
import { TooltipContent, TooltipDetail } from '../Progress/Tooltip';
import { SmallPortalTooltip } from '../../Tooltip';
import {
  Caption,
  MetricValue,
  Title,
  MetricSection,
  Row,
  MetricLabel,
  ProgressTrack,
  ProgressFill,
  formatDuration,
} from './common';
import ResultsTakeaways from './ResultsTakeaways';
import ResultsSkillsBreakdown from './ResultsSkillsBreakdown';
import { ResultsSelfAssessment } from './ResultsSelfAssessment';
import { ResultsScenarios } from './ResultsScenarios';
import ResultsOverallFingerprint from './ResultsOverallFingerprint';
import { ResultsRawAnswers } from './ResultsRawAnswers';

const Divider = styled.div`
  height: 1px;
  width: 100%;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.bgActive};
`;

const OneTwoColumnContainer = styled.div`
  display: grid;
  grid-template-columns: 0.6fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Card = styled.div<{ statusColor?: string }>`
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  ${({ statusColor }) => statusColor && `border-top: 4px solid ${statusColor};`}
  border-radius: 8px;
  padding: 24px;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  z-index: 1;
  margin-bottom: 4px;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  width: fit-content;

  ${({ status, theme }) => {
    switch (status) {
      case 'assessed':
        return `
          background-color: ${theme.colors.buttonPrimary}20;
          border: 1px solid ${theme.colors.buttonPrimary}40;
          color: ${theme.colors.buttonPrimary};
        `;
      case 'completed':
        return `
          background-color: ${theme.colors.highlight}20;
          border: 1px solid ${theme.colors.highlight}40;
          color: ${theme.colors.highlight};
        `;
      case 'in-progress':
        return `
          background-color: ${theme.colors.buttonSecondary}20;
          border: 1px solid ${theme.colors.buttonSecondary}40;
          color: ${theme.colors.buttonSecondary};
        `;
      default:
        return `
          background-color: ${theme.colors.textRegular}20;
          border: 1px solid ${theme.colors.textRegular}40;
          color: ${theme.colors.textRegular};
        `;
    }
  }}
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 24px;
`;

const InfoValue = styled(MetricValue)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 14px;
  font-weight: 400;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 20px;
  padding-bottom: 20px;
`;

const TestIcon = styled.i.attrs({
  className: 'fa-regular fa-memo-circle-info',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 14px;
  min-width: 14px;
`;

const CohortIcon = styled.i.attrs({
  className: 'fa-regular fa-calendar-range',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 14px;
  min-width: 14px;
`;

const InfoIcon = styled.i.attrs({
  className: 'fa-regular fa-circle-info',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  min-width: 12px;
  opacity: 0.6;
  cursor: help;
  margin-left: 4px;

  &:hover {
    opacity: 1;
  }
`;

export default function AssessmentTestResultPage({
  assessmentTestSessionId,
  companyId,
}: {
  assessmentTestSessionId: string;
  companyId: string;
}) {
  const { data: test } = useGetAssessmentTestSession(
    parseInt(companyId),
    parseInt(assessmentTestSessionId)
  );
  const theme = useTheme();

  const isFinished = test?.finishedAt;
  const isReviewed = test?.reviewedAt;
  const isStarted = test?.startedAt;

  const status = isReviewed
    ? 'assessed'
    : isFinished
    ? 'completed'
    : isStarted
    ? 'in-progress'
    : 'invited';
  const shouldShowResults = !!(test?.reviewedAt !== null && test?.results);

  return (
    <Wrapper>
      <OneTwoColumnContainer>
        <Column>
          <Card statusColor={getStatusColor(theme, status)}>
            <ProfileHeader>
              <Title>{test?.user?.name}</Title>
              <Caption>{test?.user?.email}</Caption>
            </ProfileHeader>
            <BasicTestDetails test={test} status={status} />
          </Card>
        </Column>

        <ResultsOverallFingerprint test={test} status={status} />
      </OneTwoColumnContainer>

      <ResultsTakeaways test={test} shouldShowResults={shouldShowResults} />

      <ResultsSkillsBreakdown test={test} />

      <ResultsSelfAssessment test={test} />

      <ResultsScenarios test={test} />

      <ResultsRawAnswers test={test} />
    </Wrapper>
  );
}

const formatDate = (date: Date) => {
  return dayjs.utc(date).format('DD MMM, HH:mm');
};

// Get status color for the gradient
const getStatusColor = (theme: DefaultTheme, status: string | undefined) => {
  switch (status) {
    case 'assessed':
      return theme.colors.buttonPrimary;
    case 'completed':
      return theme.colors.highlight;
    case 'in-progress':
      return theme.colors.buttonSecondary;
    default:
      return theme.colors.textRegular;
  }
};

function BasicTestDetails({
  test,
  status,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
  status: string;
}) {
  const numQuestions =
    test?.steps
      ?.map((step) => step.sections.length)
      .reduce((acc, curr) => acc + curr, 0) ?? 0;
  const numAnsweredQuestions =
    test?.answers
      ?.map(
        (answer) =>
          answer.sections.filter((section) => section.isAnswered).length
      )
      .reduce((acc, curr) => acc + curr, 0) ?? 0;
  const progressRate = (numAnsweredQuestions / numQuestions) * 100;

  return (
    <>
      <InfoContainer>
        <MetricSection>
          <Row style={{ gap: 24 }}>
            <Row data-tooltip-id="test-name">
              <TestIcon />
              <InfoValue> {test?.name}</InfoValue>
            </Row>
            <Row data-tooltip-id="cohort-name">
              <CohortIcon />
              <InfoValue> {test?.cohort?.name}</InfoValue>
            </Row>
          </Row>
        </MetricSection>

        {test?.name && (
          <SmallPortalTooltip anchorSelect={`[data-tooltip-id="test-name"]`}>
            <TooltipContent>
              <TooltipDetail>Assessment Test Name</TooltipDetail>
            </TooltipContent>
          </SmallPortalTooltip>
        )}
        {test?.cohort?.name && (
          <SmallPortalTooltip anchorSelect={`[data-tooltip-id="cohort-name"]`}>
            <TooltipContent>
              <TooltipDetail>Cohort Name</TooltipDetail>
            </TooltipContent>
          </SmallPortalTooltip>
        )}
      </InfoContainer>
      <Divider />
      <StatusBadge status={status} style={{ marginTop: '24px' }}>
        {status.replace('-', ' ')}
      </StatusBadge>
      <MetricsGrid>
        <MetricSection>
          <MetricLabel>Started</MetricLabel>
          <MetricValue>
            {test?.startedAt ? formatDate(test?.startedAt) : '-'}
          </MetricValue>
        </MetricSection>

        <MetricSection>
          <MetricLabel>Finished</MetricLabel>
          <MetricValue>
            {test?.finishedAt ? formatDate(test?.finishedAt) : '-'}
          </MetricValue>
        </MetricSection>
      </MetricsGrid>
      <MetricsGrid>
        <MetricSection>
          <MetricLabel>Time Spent</MetricLabel>
          <MetricValue>
            {test?.startedAt ? formatDuration(getTimeSpent(test)) : '-'}
          </MetricValue>
        </MetricSection>
        <MetricSection data-tooltip-id="finish-reason">
          <MetricLabel>
            Finish Reason
            <InfoIcon />
          </MetricLabel>
          <MetricValue>
            {test?.finishReason ? parseFinishReason(test?.finishReason) : '-'}
          </MetricValue>
        </MetricSection>
      </MetricsGrid>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="finish-reason"]`}>
        <TooltipContent>
          <TooltipDetail>
            The test can be completed by the user or automatically expires after
            1 hour
          </TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>
      <MetricSection style={{ marginTop: '24px' }}>
        <MetricLabel>Progress</MetricLabel>
        <ProgressTrack>
          <ProgressFill width={progressRate} />
        </ProgressTrack>
        <Caption>
          {test?.startedAt
            ? `${numAnsweredQuestions}/${numQuestions} questions answered`
            : 'Test not started'}
        </Caption>
      </MetricSection>
    </>
  );
}

const parseFinishReason = (finishReason: string) => {
  switch (finishReason) {
    case 'COMPLETED':
      return 'Completed';
    case 'TIME_EXPIRED':
      return 'Time Expired';
  }
  return finishReason;
};

export const getTimeSpent = (
  testResult: AssessmentTestSessionDetailResponse
) => {
  return testResult.timeSpent ?? 0;
};

const Wrapper = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 32px;
  margin-bottom: 72px;
  flex-direction: column;

  > *:first-child {
    margin-top: 32px;
  }
`;
